import { useEffect, useMemo, useState } from 'react';
import { shell } from '../../shell/shellClasses';
import { BlockCsvButton } from '../debug-export/BlockCsvButton';
import { opsCalcCsv } from '../debug-export/blockCsv';
import { downloadCsv } from '../debug-export/csv';
import { ActionCard } from './ActionCard';
import {
  loadBookmarked,
  loadDismissed,
  saveBookmarked,
  saveDismissed,
} from './sessionState';
import type { OpsFilter, OpsIntelligenceModel, OpsPriority } from './types';

const PRIORITIES: OpsPriority[] = ['critical', 'warning', 'opportunity'];

const FILTERS: { id: OpsFilter; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'critical', label: 'Critical' },
  { id: 'warning', label: 'Warning' },
  { id: 'opportunity', label: 'Opportunity' },
  { id: 'bookmarked', label: 'Bookmarked' },
];

export function OpsIntelligencePanel({
  model,
  loading,
  error,
  onRetry,
}: {
  model: OpsIntelligenceModel | null;
  loading: boolean;
  error: string | null;
  onRetry: () => void;
}) {
  const conn = model?.connectionId ?? null;
  const [filter, setFilter] = useState<OpsFilter>('all');
  const [dismissed, setDismissed] = useState<Set<string>>(() => loadDismissed(conn));
  const [bookmarked, setBookmarked] = useState<Set<string>>(() => loadBookmarked(conn));
  const [open, setOpen] = useState<Record<OpsPriority, boolean>>({
    critical: true,
    warning: true,
    opportunity: true,
  });

  useEffect(() => {
    setDismissed(loadDismissed(conn));
    setBookmarked(loadBookmarked(conn));
  }, [conn]);

  const visible = useMemo(() => {
    const items = model?.items ?? [];
    return items.filter((i) => {
      if (dismissed.has(i.id)) return false;
      if (filter === 'all') return true;
      if (filter === 'bookmarked') return bookmarked.has(i.id);
      return i.priority === filter;
    });
  }, [model?.items, dismissed, bookmarked, filter]);

  const grouped = useMemo(() => {
    const g: Record<OpsPriority, typeof visible> = {
      critical: [],
      warning: [],
      opportunity: [],
    };
    for (const i of visible) g[i.priority].push(i);
    return g;
  }, [visible]);

  const onDismiss = (id: string) => {
    setDismissed((prev) => {
      const next = new Set(prev);
      next.add(id);
      saveDismissed(conn, next);
      return next;
    });
  };

  const onToggleBookmark = (id: string) => {
    setBookmarked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      saveBookmarked(conn, next);
      return next;
    });
  };

  const counts = {
    critical: grouped.critical.length,
    warning: grouped.warning.length,
    opportunity: grouped.opportunity.length,
  };

  return (
    <section
      id="widget-ops-operations"
      data-widget-id="ops-operations"
      aria-labelledby="ops-intel-heading"
      className="space-y-4"
    >
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <h2
          id="ops-intel-heading"
          className="text-sm font-semibold tracking-tight text-[var(--shell-fg)]"
        >
          What needs action now?
        </h2>
        <BlockCsvButton
          disabled={!model}
          onClick={() => {
            if (!model) return;
            downloadCsv('dashboard_ops.csv', opsCalcCsv(model));
          }}
        />
      </div>

      <div
        role="group"
        aria-label="Filter by priority"
        className="flex flex-wrap gap-2"
      >
        {FILTERS.map((f) => {
          const on = filter === f.id;
          return (
            <button
              key={f.id}
              type="button"
              aria-pressed={on}
              onClick={() => setFilter(f.id)}
              className={`inline-flex min-h-11 items-center rounded-[var(--shell-radius-md)] border px-3 text-xs font-medium ${shell.focusRing} ${
                on
                  ? 'border-[var(--shell-border)] bg-[var(--shell-hover)] text-[var(--shell-fg)]'
                  : `${shell.border} ${shell.surfaceElevated} ${shell.muted}`
              }`}
            >
              {f.label}
              {f.id !== 'all' && f.id !== 'bookmarked' && (
                <span className={`ml-1.5 tabular-nums ${shell.subtle}`}>
                  {counts[f.id as OpsPriority]}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {error && (
        <div
          role="alert"
          className="rounded-[var(--shell-radius-md)] bg-[var(--shell-danger)]/10 px-3 py-2 text-sm text-[var(--shell-danger)]"
        >
          {error}{' '}
          <button type="button" className="underline" onClick={onRetry}>
            Try again
          </button>
        </div>
      )}

      <div className={`rounded-[var(--shell-radius-lg)] ${shell.surfaceElevated} p-3 sm:p-4`}>
        {loading && !model ? (
          <div
            className="h-40 animate-pulse rounded-[var(--shell-radius-md)] bg-[var(--shell-hover)] motion-reduce:animate-none"
            aria-busy
            aria-label="Loading operations intelligence"
          />
        ) : visible.length === 0 ? (
          <p className={`py-8 text-center text-sm ${shell.muted}`}>
            Nothing needs your attention here right now.
          </p>
        ) : (
          <div className="space-y-4">
            {PRIORITIES.map((p) => {
              const items = grouped[p];
              if (items.length === 0) return null;
              const isOpen = open[p];
              return (
                <section key={p} aria-labelledby={`ops-group-${p}`}>
                  <button
                    type="button"
                    id={`ops-group-${p}`}
                    className={`flex w-full min-h-11 items-center justify-between gap-2 rounded-[var(--shell-radius-md)] border px-3 py-2 text-left text-sm font-medium ${shell.border} ${shell.surface} ${shell.focusRing}`}
                    aria-expanded={isOpen}
                    onClick={() => setOpen((s) => ({ ...s, [p]: !s[p] }))}
                  >
                    <span className="capitalize text-[var(--shell-fg)]">
                      {p}{' '}
                      <span className={`tabular-nums ${shell.subtle}`}>({items.length})</span>
                    </span>
                    <span className={`text-xs ${shell.subtle}`}>{isOpen ? 'Collapse' : 'Expand'}</span>
                  </button>
                  {isOpen && (
                    <ul className="mt-2 space-y-2" aria-label={`${p} actions`}>
                      {items.map((item) => (
                        <li key={item.id}>
                          <ActionCard
                            item={item}
                            bookmarked={bookmarked.has(item.id)}
                            onDismiss={() => onDismiss(item.id)}
                            onToggleBookmark={() => onToggleBookmark(item.id)}
                          />
                        </li>
                      ))}
                    </ul>
                  )}
                </section>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}
